/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { useQuery } from '@apollo/client';

import { C, F } from '../constants/theme';
import {
  GOALS,
} from '../constants/data';
import {
  Btn,
  Callout,
  Heading,
  Tag,
  Fade,
  StripeTexture,
  Icon,
} from '../components';
import { Loading } from 'core/ui/components';
import { useProgramContext } from 'core/api/programContext';
import { GET_SPLIT_TEST_ORDER, GET_USER_BLOODWORK_MARKERS } from 'core/api/apollo/queries';
import useUser from 'core/hooks/useUser';

const Phase7Results = ({
  startOnramp,
  goPhase,
  scrollTop,
  bioOption,
}) => {
  const {
    oneTimeProgramId,
    goals,
    specs,
    daysLabel,
    progLabel,
    sizeLabel,
    flagCount,
    bioFlags,
  } = useProgramContext();
  const { currentUser } = useUser();

  const [expandedMeal, setExpandedMeal] = useState(null);

  const name = currentUser?.firstName || '';
  // const mealCount = 10;

  const { loading, data: { getSplitTestOrder = [] } = {} } = useQuery(GET_SPLIT_TEST_ORDER, {
    variables: { oneTimeProgramId },
    fetchPolicy: 'network-only',
    skip: !oneTimeProgramId,
  });

  const { data: { listUserBloodworkMarkers = [] } = {} } = useQuery(GET_USER_BLOODWORK_MARKERS, {
    fetchPolicy: 'network-only',
  });

  // eslint-disable-next-line no-console
  console.log('userBloodworkMarkers', listUserBloodworkMarkers);
  // eslint-disable-next-line no-console
  console.log('getSplitTestOrder', getSplitTestOrder);

  if (loading) {
    return <Loading />;
  }

  // eslint-disable-next-line no-console
  console.log('Object.entries(bioFlags)', Object.entries(bioFlags));

  return (
    <div style={{ fontFamily: F.b, color: C.darkBrown, background: C.white, minHeight: '100vh', overflowY: 'auto' }}>
      {/* Header */}
      <div style={{ position: 'relative', background: C.darkGreen, padding: '32px 24px 28px', textAlign: 'center', overflow: 'hidden' }}>
        <StripeTexture color="#000000" opacity={0.10}/>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ textTransform: 'uppercase', fontSize: 9, fontWeight: 700, letterSpacing: '2.5px', color: C.lightGreen, marginBottom: 8 }}>YOUR {progLabel} PLAN</div>
          <h1 style={{ fontFamily: F.h, fontSize: 30, fontWeight: 400, color: C.pureWhite, margin: '0 0 8px', textWrap: 'balance' }}>{name || 'Your'}, your menu is ready.</h1>
          <p style={{ fontSize: 13, color: C.lightGreen, margin: '0 0 16px' }}>{flagCount > 0 ? `We identified ${flagCount} biomarker(s) that need attention. Every meal below is weighted to help move them toward optimal.` : `Based on your goals, taste, and lifestyle, here's your personalized ${progLabel} lineup.`}</p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 24 }}>
            {[{ v: specs.prot, l: 'protein per meal' }, { v: specs.cal, l: 'cal per meal' }].map((m, i) => <div key={i} style={{ textAlign: 'center' }}><div style={{ fontFamily: F.h, fontSize: 18, fontWeight: 500, color: C.pureWhite }}>{m.v}</div><div style={{ fontSize: 9, color: C.lightGreen, letterSpacing: '0.8px', textTransform: 'uppercase', marginTop: 2 }}>{m.l}</div></div>)}
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
            {goals.slice(0, 4).map((g) => { const go = GOALS.find((x) => x.id === g); return go ? <Tag key={g} color={C.lightGreen} bg="rgba(255,255,255,0.1)"><Icon name={go.icon} size={10} color={C.lightGreen}/> {go.label}</Tag> : null; })}
          </div>
        </div>
      </div>

      {/* Biomarker Dashboard */}
      {listUserBloodworkMarkers?.length > 0 && <div style={{ maxWidth: 580, margin: '0 auto', padding: '20px 20px 0' }}>
        <Callout>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.darkBrown, marginBottom: 6 }}>
            What {bioOption === 'flag' ? "you've told us" : 'your bloodwork tells us'}
          </div>
          {listUserBloodworkMarkers.map((marker) =>
            <div key={marker?.id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
              <span style={{ fontSize: 12 }}>{marker?.status === 'TOO_HIGH' ? '⬆️' : '⬇️'}</span>
              <span style={{ fontSize: 12, color: C.brown }}>
                {marker?.bloodworkMarker?.name} ({marker?.status.replace(/_/g, ' ')})
              </span>
            </div>
          )}
          <div style={{ fontSize: 11, color: C.brown, marginTop: 8 }}>
            Every meal in your plan will be weighted to help move {flagCount === 1 ? 'this' : 'these'} toward optimal.
          </div>
        </Callout>
      </div>}

      {/* Personalized Menu */}
      <div style={{ maxWidth: 580, margin: '0 auto', padding: '16px 20px 48px' }}>
        <Heading size={22} style={{ marginBottom: 4 }}>Your First Week, Mapped Out</Heading>
        <p style={{ fontSize: 12, color: C.lightBrown, margin: '0 0 16px' }}>Every meal below is matched to your profile. After you start, you can swap anything you'd like.</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {getSplitTestOrder.map((m, i) => (
            <Fade key={m.id} delay={i * 60}>
              <div style={{ background: C.pureWhite, borderRadius: 0, border: `${i < 3 ? '2px' : '1px'} solid ${i < 3 ? C.mustard : '#E8E4DC'}`, overflow: 'hidden', cursor: 'pointer' }} onClick={() => setExpandedMeal(expandedMeal === m.id ? null : m.id)}>
                {i < 3 && <div style={{ background: C.mustard, color: C.pureWhite, fontSize: 9, fontWeight: 700, letterSpacing: '1px', padding: '3px 12px', textAlign: 'center' }}>#{i + 1} MATCH</div>}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '14px 18px 10px' }}>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '1.8px', color: C.lightBrown, textTransform: 'uppercase' }}>{m.cuisine}</div>
                    <div style={{ fontFamily: F.h, fontSize: 17, fontWeight: 500, color: C.darkBrown, marginTop: 3, lineHeight: 1.2 }}>{m.productVariantName}</div>
                  </div>
                  {/* <div style={{ width: 40, height: 40, borderRadius: '50%', border: `2px solid ${m.score >= 90 ? C.green : C.mustard}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: m.score >= 90 ? C.green : C.mustard }}>{Math.round(m.score)}</span>
                  </div> */}
                </div>
                <div style={{ display: 'flex', gap: 16, padding: '0 18px 12px' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 3 }}><span style={{ fontSize: 15, fontWeight: 700, color: C.darkBrown }}>{Math.round(m.calories)}</span><span style={{ fontSize: 10, color: C.lightBrown }}>cal</span></div>
                  <Tag color={C.green}>{Math.round(m.proteinGrams)}g protein</Tag>
                </div>
                {/* {expandedMeal === m.id && <div style={{ padding: '12px 18px 16px', background: C.lightBeige, borderTop: '1px solid #E8E4DC' }}><div style={{ fontSize: 11, fontWeight: 700, color: C.darkBrown, marginBottom: 4 }}>Why this matches you</div><div style={{ fontSize: 12, color: C.brown, lineHeight: 1.55 }}>{m?.why}</div></div>} */}
              </div>
            </Fade>
          ))}
        </div>

        {/* Final CTA */}
        <div style={{ position: 'relative', background: C.darkGreen, borderRadius: 0, padding: '28px 24px', marginTop: 28, textAlign: 'center', overflow: 'hidden' }}>
          <StripeTexture color="#000000" opacity={0.10}/>
          <div style={{ position: 'relative', zIndex: 1 }}>
            <h3 style={{ fontFamily: F.h, fontSize: 22, fontWeight: 400, color: C.pureWhite, margin: '0 0 6px' }}>
              Ready to eat for your biology?
            </h3>
            <p style={{ fontSize: 12, color: C.lightGreen, margin: '0 0 4px' }}>
              {progLabel} · {sizeLabel} · {daysLabel}
            </p>
            <p style={{ fontSize: 12, color: C.lightGreen, margin: '0 0 16px' }}>
              {specs.prot} protein · {specs.cal} cal per meal
            </p>
            <p style={{ fontSize: 12, color: C.pureWhite, margin: '0 0 16px' }}>
              By proceeding, you are subscribing to a weekly meal delivery program that will automatically renew each week until you cancel. Full terms will be presented before payment.
            </p>
            <Btn onClick={() => { goPhase(8, 0); scrollTop(); }} style={{ background: C.pureWhite, color: C.darkGreen }}>
              Start My Plan →
            </Btn>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 14 }}>
              {['No commitment', 'Skip or cancel anytime', 'Secure checkout'].map((t) =>
              <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Check size={11} color={C.lightGreen} strokeWidth={2.5}/>
                <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)' }}>{t}</span>
              </div>)}
            </div>
          </div>
        </div>
        <div style={{ textAlign: 'center', marginTop: 16 }}><span style={{ fontSize: 12, color: C.lightBrown, cursor: 'pointer' }} onClick={startOnramp}>Start over with different preferences</span></div>
      </div>
    </div>
  );
};

export default Phase7Results;
